import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import DigitsCodeInput from '../../components/DigitsCodeInput/DigitsCodeInput';
import Icon, { Icons } from '../../components/Icon/Icon';
import PageHeader from '../../components/PageHeader';
import { t } from '../../lang';
import HelperService from '../../services/HelperService';
import RequestService from '../../services/RequestService';
import TypesService from '../../services/TypesService';

import './VerificationEmailPhone.scss';

const codeLength = TypesService.DigitsCodeLength;

const LOADING_ICON_SIZE = 20;

const VerificationEmailPhone = () => {
	const [verifyLoading, setVerifyLoading] = useState(false);
	const [emailCode, setEmailCode] = useState('');
	const [phoneCode, setPhoneCode] = useState('');
	const [emailResendTimer, setEmailResendTimer] = useState(0);
	const [phoneResendTimer, setPhoneResendTimer] = useState(0);
	const [emailCodeError, setEmailCodeError] = useState(false);
	const [phoneCodeError, setPhoneCodeError] = useState(false);
	const [emailCodeSendingLoading, setEmailCodeSendingLoading] = useState(false);
	const [phoneCodeSendingLoading, setPhoneCodeSendingLoading] = useState(false);

	const navigate = useNavigate();
	const location = useLocation();

	const {
		verificationEmail,
		verificationPhone,
		successNotifficationText,
		successRedirectRoute,
		emailCodeResendAvailableTime,
		phoneCodeResendAvailableTime,
	} = location.state || {};

	useEffect(() => {
		if (emailResendTimer > 0) setTimeout(setEmailResendTimer, 1000, emailResendTimer - 1);
	}, [emailResendTimer]);

	useEffect(() => {
		if (phoneResendTimer > 0) setTimeout(setPhoneResendTimer, 1000, phoneResendTimer - 1);
	}, [phoneResendTimer]);

	useEffect(() => {
		if (emailCodeResendAvailableTime > 0) setEmailResendTimer(emailCodeResendAvailableTime);
		if (phoneCodeResendAvailableTime > 0) setPhoneResendTimer(phoneCodeResendAvailableTime);
	}, []);

	const verifyCodes = async () => {
		try {
			setVerifyLoading(true);
			const response = await RequestService.VerificationEmailPhone(emailCode, phoneCode);

			if (response?.Status === 'Error') {
				if (response?.ErrorCode === 'EmailCodeError') {
					setEmailCodeError(true);
				} else if (response?.ErrorCode === 'PhoneCodeError') {
					setPhoneCodeError(true);
				} else {
					navigate(-1);
					RequestService.BaseErrorFunc(response);
				}
			} else {
				if (successNotifficationText) {
					HelperService.showNotification({ type: 'success', title: t(successNotifficationText) });
				}
				navigate(successRedirectRoute);
			}
		} catch (error) {
			RequestService.BaseErrorFunc(error);
		} finally {
			setVerifyLoading(false);
		}
	};

	const emailCodeFillHandler = () => {
		if (!verificationPhone || phoneCode.length === codeLength) verifyCodes();
	};

	const phoneCodeFillHandler = () => {
		if (!verificationEmail || emailCode.length === codeLength) verifyCodes();
	};

	const resentCode = async (email, phone) => {
		try {
			if (email) setEmailCodeSendingLoading(true);
			if (phone) setPhoneCodeSendingLoading(true);

			const response = await RequestService.ResendVerificationCode(email, phone);

			if (response?.Status === 'Success') {
				if (email) {
					setEmailResendTimer(60);
					setEmailCode('');
				}
				if (phone) {
					setPhoneResendTimer(60);
					setPhoneCode('');
				}
			} else RequestService.BaseErrorFunc(response);
		} catch (error) {
			RequestService.BaseErrorFunc(error);
		} finally {
			setEmailCodeSendingLoading(false);
			setPhoneCodeSendingLoading(false);
		}
	};

	const resentEmailCode = () => {
		resentCode(true, false);
	};

	const resentPhoneCode = () => {
		resentCode(false, true);
	};

	const defaultSubheader = `Enter the 6-digit verification code we send to your${verificationEmail ? ' email' : ''}${
		verificationEmail && verificationPhone ? ' and' : ''
	}${verificationPhone ? ' phone number' : ''} to contune.`;

	if (!verificationEmail && !verificationPhone) return null;

	const resendBox = (loading, timer, resendHandler) => {
		return (
			<div className='VerificationEmailPhone__resent-box'>
				{loading ? (
					<Icon type={Icons.LOADING_SPINNER} width={LOADING_ICON_SIZE} height={LOADING_ICON_SIZE} />
				) : timer ? (
					<>
						{t('Resend code in')}
						<span className='VerificationEmailPhone__resent-timer'>{timer}</span>
					</>
				) : (
					<Button
						className='VerificationEmailPhone__resent-button'
						btnSize='small'
						styleType='ghost'
						onClick={resendHandler}
					>
						{t('Resend code')}
					</Button>
				)}
			</div>
		);
	};

	return (
		<div className='VerificationEmailPhone'>
			<PageHeader header={'6-digit code'} subheader={defaultSubheader} />
			<div className='VerificationEmailPhone__body'>
				{verificationEmail && (
					<div className='VerificationEmailPhone__email-input-box'>
						<div className='VerificationEmailPhone__input-header'>Email</div>
						<DigitsCodeInput
							className='VerificationEmailPhone__DigitsCodeInput'
							value={emailCode}
							onChange={setEmailCode}
							onFill={emailCodeFillHandler}
							error={emailCodeError}
							disabled={verifyLoading}
						/>
						{resendBox(emailCodeSendingLoading, emailResendTimer, resentEmailCode)}
					</div>
				)}
				{verificationPhone && (
					<div className='VerificationEmailPhone__phone-input-box'>
						<div className='VerificationEmailPhone__input-header'>Phone</div>
						<DigitsCodeInput
							className='VerificationEmailPhone__DigitsCodeInput'
							value={phoneCode}
							onChange={setPhoneCode}
							onFill={phoneCodeFillHandler}
							error={phoneCodeError}
							disabled={verifyLoading}
							autoFocus={!verificationEmail}
						/>
						{resendBox(phoneCodeSendingLoading, phoneResendTimer, resentPhoneCode)}
					</div>
				)}
			</div>
		</div>
	);
};

export default VerificationEmailPhone;
